/**
 * Conciliação bancária dos recebíveis de cartão — a conferência FINAL.
 *
 * São três checagens em cadeia:
 *   1. agenda prevista   — o que deveríamos receber
 *   2. planilha da Cielo — o que a adquirente diz que pagou
 *   3. extrato bancário  — o que de fato entrou na conta  ← esta tela
 *
 * O casamento é por DIA: o depósito da adquirente chega agregado, então o dia é
 * a menor unidade em que os dois lados falam a mesma língua.
 */
import React, { useState } from 'react';
import { RefreshCw, CheckCircle2, AlertTriangle, FileQuestion, MinusCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { Card, CardContent } from '@components/Card';
import { financialReportsService } from '@services/financialReportsService';
import { formatCurrencyBRL, formatDateBR } from './financialUtils';
import { inputCls } from './financialShared';
import type { BankMatchCategory, BankReconciliationReport } from '@/types/cardReceivable';

interface BankReconciliationPanelProps {
  /** Empresa e conta vêm da tela de Conta Corrente. */
  companyId: number;
  accountId: number;
  accountLabel: string;
}

/** Os valores chegam em centavos; a exibição é sempre pt-BR (R$ 1.234,56). */
const formatCurrency = (cents: number): string => formatCurrencyBRL(cents / 100);

/** Datas: sempre dd/MM/aaaa, via o helper compartilhado do módulo. */
const formatDate = (iso: string): string => formatDateBR(iso);

const startOfWeek = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
};
const today = (): string => new Date().toISOString().slice(0, 10);

// A conciliação bancária usa as MESMAS quatro categorias de casamento, mas
// nunca a "já importado" — esta é da conferência da planilha, onde o mesmo
// arquivo pode ser reenviado. Por isso o Record exclui a categoria em vez de
// inventar um rótulo que jamais aparece.
const CATEGORY_META: Record<
  BankMatchCategory,
  { label: string; className: string; icon: React.ReactNode }
> = {
  divergente: { label: 'Divergente', className: 'bg-red-50 border-red-200 text-red-700', icon: <AlertTriangle size={14} /> },
  so_no_arquivo: { label: 'Só no banco', className: 'bg-amber-50 border-amber-200 text-amber-700', icon: <FileQuestion size={14} /> },
  so_na_tabela: { label: 'Só na agenda', className: 'bg-orange-50 border-orange-200 text-orange-700', icon: <MinusCircle size={14} /> },
  confere: { label: 'Confere', className: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: <CheckCircle2 size={14} /> },
};

export const BankReconciliationPanel: React.FC<BankReconciliationPanelProps> = ({
  companyId,
  accountId,
  accountLabel,
}) => {
  const [dateFrom, setDateFrom] = useState(startOfWeek());
  const [dateTo, setDateTo] = useState(today());

  const [report, setReport] = useState<BankReconciliationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setReport(await financialReportsService.getBankReconciliation(companyId, accountId, dateFrom, dateTo));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao conciliar o extrato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        O último elo: compara, dia a dia, o que a agenda diz que a adquirente creditaria com o que
        de fato entrou na conta. A fonte é o que já está lançado na conta corrente — quando a
        integração com o extrato do Itaú estiver no ar, ela alimenta esta mesma comparação sozinha.
      </p>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <p className="text-sm text-slate-700 mb-3">
            Conciliando <strong>{accountLabel}</strong>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">De</label>
              <DateInputBR className={inputCls} value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Até</label>
              <DateInputBR className={inputCls} value={dateTo} onChange={setDateTo} />
            </div>
            <Button
              size="sm"
              loading={loading}
              onClick={load}
              icon={<RefreshCw size={14} />}
            >
              Conciliar
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => { setDateFrom(today()); setDateTo(today()); }}
            >
              Hoje
            </button>
            <span className="text-xs text-slate-300">·</span>
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => { setDateFrom(startOfWeek()); setDateTo(today()); }}
            >
              Esta semana
            </button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card>
              <CardContent>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Previsto pela agenda</p>
                <p className="text-lg font-bold text-slate-800 mt-1">{formatCurrency(report.expected_net)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Creditado na conta</p>
                <p className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(report.bank_net)}</p>
              </CardContent>
            </Card>
            <Card className={report.diff_net === 0 ? '' : 'bg-red-50 border-red-200'}>
              <CardContent>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Diferença</p>
                <p className={`text-lg font-bold mt-1 ${report.diff_net === 0 ? 'text-slate-800' : 'text-red-600'}`}>
                  {report.diff_net > 0 ? '+' : ''}{formatCurrency(report.diff_net)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <th className="text-left font-medium py-2.5 px-4">Situação</th>
                    <th className="text-left font-medium py-2.5">Dia</th>
                    <th className="text-right font-medium py-2.5">Agenda</th>
                    <th className="text-right font-medium py-2.5">Conta</th>
                    <th className="text-right font-medium py-2.5 px-4">Diferença</th>
                  </tr>
                </thead>
                <tbody>
                  {report.days.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-slate-500 py-8">
                        Nenhum movimento de cartão no período — nem na agenda, nem na conta.
                      </td>
                    </tr>
                  ) : (
                    report.days.map((day) => {
                      const meta = CATEGORY_META[day.category];
                      return (
                        <tr key={day.date} className="border-b border-slate-100">
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.className}`}>
                              {meta.icon}
                              {meta.label}
                            </span>
                            {day.reason && <p className="text-xs text-slate-500 mt-1 max-w-sm">{day.reason}</p>}
                          </td>
                          <td className="py-2.5 whitespace-nowrap">{formatDate(day.date)}</td>
                          <td className="py-2.5 text-right">
                            {formatCurrency(day.expected_net)}
                            <span className="block text-xs text-slate-400">{day.expected_count} parcela(s)</span>
                          </td>
                          <td className="py-2.5 text-right">
                            {formatCurrency(day.bank_net)}
                            <span className="block text-xs text-slate-400">{day.bank_count} lançamento(s)</span>
                          </td>
                          <td className="py-2.5 px-4 text-right font-medium">
                            <span className={day.diff_net === 0 ? 'text-slate-400' : day.diff_net > 0 ? 'text-emerald-700' : 'text-red-600'}>
                              {day.diff_net > 0 ? '+' : ''}{formatCurrency(day.diff_net)}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Estado por depósito. A tabela acima diz se o total do dia bate; esta
              diz quais créditos o banco já confirmou — e essa resposta fica
              gravada, em vez de ser recalculada a cada consulta. */}
          {report.settlements && (
            <Card>
              <CardContent>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <p className="text-sm font-medium text-slate-800">Confirmação dos depósitos pelo banco</p>
                  <div className="flex gap-4 text-xs">
                    <span className="text-emerald-700">
                      {report.settlements.count_reconciled} confirmado(s) ·{' '}
                      {formatCurrency(report.settlements.reconciled_net)}
                    </span>
                    <span className="text-slate-600">
                      {report.settlements.count_pending} aguardando ·{' '}
                      {formatCurrency(report.settlements.pending_net)}
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                        <th className="text-left font-medium py-2">Situação</th>
                        <th className="text-left font-medium py-2">Dia</th>
                        <th className="text-left font-medium py-2">Bandeira</th>
                        <th className="text-right font-medium py-2">Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.settlements.settlements.map((s) => (
                        <tr key={s.settlement_id} className="border-b border-slate-100 last:border-0">
                          <td className="py-2">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${
                                s.reconciled
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                  : 'bg-slate-50 border-slate-200 text-slate-600'
                              }`}
                            >
                              {s.reconciled ? <CheckCircle2 size={13} /> : <MinusCircle size={13} />}
                              {s.reconciled ? 'Confirmado' : 'Aguardando'}
                            </span>
                            <p className="text-xs text-slate-500 mt-1 max-w-md">
                              {s.reason}
                              {s.bank_reconciled_on && ` em ${formatDate(s.bank_reconciled_on)}`}
                            </p>
                          </td>
                          <td className="py-2 whitespace-nowrap">{formatDate(s.settlement_date)}</td>
                          <td className="py-2 text-slate-600">
                            {s.brand_label || s.brand}
                            {s.kind && <span className="block text-xs text-slate-400">{s.kind}</span>}
                          </td>
                          <td className="py-2 text-right font-medium">{formatCurrency(s.net_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-slate-500 mt-3">
                  A confirmação é gravada quando o extrato bancário traz o crédito — na importação do
                  extrato ou quando a conferência da Cielo encontra o valor já na conta. Importe o
                  extrato do período para confirmar o que está aguardando.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
